import React, { useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Database,
  ShieldCheck,
  CalendarDays,
  Save,
  ArrowLeft,
  Loader2,
  Info,
  UserRound,
  MapPin,
  FileText,
  Settings2,
} from "lucide-react";

import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";

const API_BASE = (
  process.env.REACT_APP_API_URL || "https://supremeadmin.nissi-india.com"
).replace(/\/+$/, "");

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 600000,
});

const DB_TYPES = ["MASTER", "INVENTORY", "PAYROLL", "TALLY"];

const CURRENT_YEAR = new Date().getFullYear();

const FINANCIAL_YEARS = Array.from({ length: 11 }, (_, index) => {
  const year = CURRENT_YEAR - 5 + index;
  return `${year}-${year + 1}`;
});

const DEFAULT_FEATURES = JSON.stringify(
  {
    modules: ["master", "inventory", "payroll", "tally"],
  },
  null,
  2
);

function generateTenantCode(name) {
  const code = String(name || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 20);
  return code || "TENANT";
}

function CompanyLogoUpload({ value, onChange, disabled = false }) {
  const [error, setError] = React.useState("");
  const handleFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      setError("Please select a PNG, JPG or WebP image.");
      event.target.value = "";
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError("Logo must be 2 MB or smaller.");
      event.target.value = "";
      return;
    }
    setError("");
    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result || ""));
    reader.onerror = () => setError("Could not read the selected image.");
    reader.readAsDataURL(file);
    event.target.value = "";
  };
  return (
    <div className="space-y-3">
      <label className="block text-sm font-semibold text-slate-700">Company Logo</label>
      <div className="flex flex-wrap items-center gap-4 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
        <div className="flex h-24 w-32 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white">
          {value ? (
            <img src={value} alt="Company logo preview" className="h-full w-full object-contain" />
          ) : (
            <span className="text-xs text-slate-400">No logo selected</span>
          )}
        </div>
        <div className="space-y-2">
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleFile}
            disabled={disabled}
            className="block max-w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-emerald-700 file:px-4 file:py-2 file:text-white hover:file:bg-emerald-800 disabled:opacity-50"
          />
          <p className="text-xs text-slate-500">PNG, JPG or WebP, up to 2 MB.</p>
          {value && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                setError("");
                onChange("");
              }}
              className="text-sm font-medium text-rose-600 hover:underline disabled:opacity-50"
            >
              Remove logo
            </button>
          )}
        </div>
      </div>
      {error && (
        <p role="alert" className="text-sm text-rose-600">
          {error}
        </p>
      )}
    </div>
  );
}

const INITIAL_FORM = {
  logo: "",
  tenantCode: "",
  name: "",
  legalName: "",
  contactPerson: "",
  email: "",
  phone: "",
  alternatePhone: "",
  website: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  stateName: "",
  postalCode: "",
  country: "India",
  gstin: "",
  pan: "",
  domain: "",
  financialYear: `${CURRENT_YEAR}-${CURRENT_YEAR + 1}`,
  plan: "STARTER",
  maxUsers: 5,
  expiresOn: "",
  graceDays: 7,
  featuresJson: DEFAULT_FEATURES,

  // Inventory Customer Options (tbl_CustomerOptionsFromSuperAdmin)
  fld_Site: "",
  fld_SiteName: "",
  fld_NoofBranches: 1,
  fld_NoofWorkLocations: 1,
  fld_NoofStores: 1,
  fld_NoofAccountsIntegration: 0,
  fld_NoofProjects: 0,
  fld_VehicleConnectInventory: 0,
  
  fld_jobcard: 0,
 
  fld_bulkinsert: 0,
  fld_sundayleave: 0,
  fld_MultiUserBlock: 0,
  fld_invdashboardalert: 1,
  fld_MultiUserAllowIP: "",
  fld_termsstatus: 0,
  fld_termsVisible: 0,
  fld_finyearallowchange: 1,
  fld_NoofSubBranches: 0,
  fld_lockdownmonthlyAtten: 0,
  fld_appconnectionerp: 1,
  fld_appconnectionpayroll: 1,
  fld_maindb: "",
  fld_activedb: "",
  fld_finyearDBpath: "",
  fld_istermslink: 0,
  fld_termslinkname: "",
  fld_noofproductsinventory: 0,
  fld_noofvehicle: 0,
  fld_noofpayroll: 0,
  fld_noofasset: 0,
  fld_video: 0,
  fld_lang: 1,
};

function getErrorMessage(error) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Unable to register tenant."
  );
}

function escapeHtml(value) {
  return String(value || "").replace(/[&<>"']/g, (character) => {
    const replacements = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    };
    return replacements[character];
  });
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center gap-2 border-b border-slate-200 bg-emerald-50 px-5 py-3">
        <Icon size={18} className="text-emerald-700" />
        <h2 className="text-sm font-bold text-slate-800">{title}</h2>
      </div>
      <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">{children}</div>
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  placeholder = "",
  required = false,
  disabled = false,
  min,
  max,
  maxLength,
  readOnly = false,
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}
        {required && <span className="ml-1 text-rose-500">*</span>}
      </span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        min={min}
        max={max}
        maxLength={maxLength}
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 disabled:bg-slate-100 read-only:bg-slate-50"
      />
    </label>
  );
}

export default function Tenant_Add() {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);

  const setField = (key, value) => {
    setForm((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const databaseNames = useMemo(() => {
    const slug = form.name.toLowerCase().replace(/[^a-z0-9]/g, "");
    if (!slug) {
      return {
        MASTER: "",
        INVENTORY: "",
        PAYROLL: "",
        TALLY: "",
      };
    }
    return {
      MASTER: `infra_${slug}_master`,
      INVENTORY: `infra_${slug}_inventory_${form.financialYear}`,
      PAYROLL: `infra_${slug}_payroll_${form.financialYear}`,
      TALLY: `infra_${slug}_tally_${form.financialYear}`,
    };
  }, [form.name, form.financialYear]);

  // Auto-fill DB path fields when company name or financial year changes
  React.useEffect(() => {
    setForm((prev) => ({
      ...prev,
      fld_maindb: databaseNames.MASTER || prev.fld_maindb,
      fld_activedb: databaseNames.INVENTORY || prev.fld_activedb,
      fld_finyearDBpath: databaseNames.INVENTORY || prev.fld_finyearDBpath,
    }));
  }, [databaseNames]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;

    // ===== VALIDATIONS =====
    if (!/^[A-Z0-9_]{3,20}$/.test(form.tenantCode)) {
      return Swal.fire(
        "Validation",
        "Tenant code must contain 3-20 uppercase letters, numbers or underscores.",
        "warning"
      );
    }

    if (!form.name.trim() || !form.legalName.trim()) {
      return Swal.fire(
        "Validation",
        "Company name and legal name are required.",
        "warning"
      );
    }

    if (!form.financialYear) {
      return Swal.fire("Validation", "Please select a financial year.", "warning");
    }

    if (Object.values(databaseNames).some((name) => !name || name.length > 128)) {
      return Swal.fire(
        "Validation",
        "Generated database names are invalid or too long.",
        "warning"
      );
    }

    // Email
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      return Swal.fire("Validation", "Please enter a valid email address.", "warning");
    }

    // Phone (10 digit Indian mobile)
    if (form.phone && !/^[6-9]\d{9}$/.test(form.phone.trim())) {
      return Swal.fire(
        "Validation",
        "Phone number must be a valid 10-digit Indian mobile number.",
        "warning"
      );
    }

    if (form.alternatePhone && !/^[6-9]\d{9}$/.test(form.alternatePhone.trim())) {
      return Swal.fire(
        "Validation",
        "Alternate phone must be a valid 10-digit Indian mobile number.",
        "warning"
      );
    }

    // Website
    if (form.website && !/^https?:\/\/.+\..+/.test(form.website.trim())) {
      return Swal.fire("Validation", "Please enter a valid website URL (starting with http:// or https://).", "warning");
    }

    // GSTIN
    if (form.gstin && !/^\d{2}[A-Z]{5}\d{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/.test(form.gstin.toUpperCase())) {
      return Swal.fire("Validation", "Invalid GSTIN format.", "warning");
    }

    // PAN
    if (form.pan && !/^[A-Z]{5}\d{4}[A-Z]$/.test(form.pan.toUpperCase())) {
      return Swal.fire("Validation", "Invalid PAN format.", "warning");
    }

    const maxUsers = Number(form.maxUsers);
    const graceDays = Number(form.graceDays);

    if (!Number.isInteger(maxUsers) || maxUsers < 1) {
      return Swal.fire("Validation", "Maximum users must be at least 1.", "warning");
    }

    if (!Number.isInteger(graceDays) || graceDays < 0 || graceDays > 3650) {
      return Swal.fire("Validation", "Grace days must be between 0 and 3650.", "warning");
    }

    try {
      JSON.parse(form.featuresJson);
    } catch {
      return Swal.fire("Validation", "Features JSON is invalid.", "warning");
    }

    const confirmation = await Swal.fire({
      title: "Register Infra V5 Tenant?",
      html: `
        <div style="text-align:left;font-size:14px">
          <p><b>Company:</b> ${escapeHtml(form.name)}</p>
          <p><b>Tenant Code:</b> ${escapeHtml(form.tenantCode)}</p>
          <p><b>Financial Year:</b> ${escapeHtml(form.financialYear)}</p>
          <p><b>Databases:</b> 4 will be created & cleaned</p>
          <p style="margin-top:10px;color:#b45309;font-weight:500">
            This will create the tenant, SQL login, restore the 4 databases, map the login, and clean data (keeping tbl_country_master, tbl_state_master, tbl_city_master, tbl_district_master in Inventory only). This may take several minutes.
          </p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Register & Provision",
      confirmButtonColor: "#059669",
    });

    if (!confirmation.isConfirmed) return;

    setSaving(true);

    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        legalName: form.legalName.trim(),
        tenantCode: form.tenantCode.trim().toUpperCase(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        alternatePhone: form.alternatePhone.trim() || null,
        gstin: form.gstin.trim().toUpperCase() || null,
        pan: form.pan.trim().toUpperCase() || null,
        maxUsers,
        graceDays,
        expiresOn: form.expiresOn || null,
      };

      const response = await api.post("/api/tenants/register-infra", payload);

      if (response.data?.success === false || response.data?.ok === false) {
        throw new Error(response.data?.message || "Tenant registration failed.");
      }

      const data = response.data;
      const creds = data.credentials || {};

      await Swal.fire({
        icon: "success",
        title: "Tenant Registered & Provisioned",
        html: `
          <div style="text-align:left;font-size:14px;line-height:1.65">
            <p><b>Company:</b> ${escapeHtml(form.name)}</p>
            <p><b>Tenant Code:</b> ${escapeHtml(form.tenantCode)}</p>
            <hr style="margin:14px 0;border-color:#e2e8f0"/>
            <p style="color:#059669;font-weight:600;margin-bottom:6px">Database Credentials</p>
            <p><b>SQL Login:</b> <code style="background:#f1f5f9;padding:2px 6px;border-radius:4px">${escapeHtml(creds.sqlLogin || "—")}</code></p>
            <p><b>Password:</b> <code style="background:#f1f5f9;padding:2px 6px;border-radius:4px">${escapeHtml(creds.sqlPassword || "—")}</code></p>
            <p style="margin-top:10px;font-size:12px;color:#64748b">
              Save these credentials securely. They will not be shown again.
            </p>
            <hr style="margin:14px 0;border-color:#e2e8f0"/>
            <p><b>Databases created & cleaned:</b></p>
            <ul style="margin:6px 0 0 18px;padding:0">
              ${Object.entries(data.databases || {})
                .map(
                  ([type, name]) =>
                    `<li><b>${escapeHtml(type)}:</b> ${escapeHtml(name)}</li>`
                )
                .join("")}
            </ul>
            <p style="margin-top:10px;font-size:12px;color:#059669">
              Inventory DB keeps tbl_country_master, tbl_state_master, tbl_city_master, tbl_district_master. All other tables were cleared.
            </p>
          </div>
        `,
        confirmButtonText: "Done",
        confirmButtonColor: "#059669",
        width: 540,
      });

      navigate("/supremeadmin/tenant");
    } catch (error) {
      console.error("Tenant registration failed:", error);
      await Swal.fire({
        icon: "error",
        title: "Registration Failed",
        text: getErrorMessage(error),
        confirmButtonColor: "#059669",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="flex min-h-screen flex-col md:flex-row">
        <SuperAdminNav />
        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-6 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-600 text-white">
                <Building2 size={22} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Add Infra V5 Tenant</h1>
                <p className="text-sm text-slate-500">Company registration + automatic database provisioning</p>
              </div>
            </div>
            <Time />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <Section title="Company Branding" icon={Building2}>
              <div className="md:col-span-2">
                <CompanyLogoUpload
                  value={form.logo}
                  onChange={(value) => setField("logo", value)}
                  disabled={saving}
                />
              </div>
            </Section>

            <Section title="Tenant Information" icon={Building2}>
              <Field
                label="Company Name"
                value={form.name}
                onChange={(value) => {
                  setField("name", value);
                  setField("tenantCode", generateTenantCode(value));
                }}
                placeholder="ABC Construction"
                required
                disabled={saving}
                maxLength={200}
              />
              <Field
                label="Tenant Code (auto-generated)"
                value={form.tenantCode}
                onChange={(value) =>
                  setField("tenantCode", value.toUpperCase().replace(/[^A-Z0-9_]/g, ""))
                }
                placeholder="Auto from company name"
                required
                disabled={saving}
                maxLength={20}
              />
              <Field
                label="Legal Company Name"
                value={form.legalName}
                onChange={(value) => setField("legalName", value)}
                placeholder="ABC Construction Private Limited"
                required
                disabled={saving}
                maxLength={200}
              />
              <Field
                label="Domain"
                value={form.domain}
                onChange={(value) => setField("domain", value)}
                placeholder="abcconstruction.example.com"
                disabled={saving}
              />
            </Section>

            <Section title="Company Contact Details" icon={UserRound}>
              <Field
                label="Contact Person"
                value={form.contactPerson}
                onChange={(value) => setField("contactPerson", value)}
                placeholder="Managing Director"
                disabled={saving}
              />
              <Field
                label="Email Address"
                type="email"
                value={form.email}
                onChange={(value) => setField("email", value)}
                placeholder="admin@example.com"
                disabled={saving}
              />
              <Field
                label="Phone Number"
                value={form.phone}
                onChange={(value) => setField("phone", value.replace(/\D/g, "").slice(0, 10))}
                placeholder="9876543210"
                disabled={saving}
                maxLength={10}
              />
              <Field
                label="Alternate Phone"
                value={form.alternatePhone}
                onChange={(value) => setField("alternatePhone", value.replace(/\D/g, "").slice(0, 10))}
                placeholder="9876543210"
                disabled={saving}
                maxLength={10}
              />
              <Field
                label="Website"
                value={form.website}
                onChange={(value) => setField("website", value)}
                placeholder="https://example.com"
                disabled={saving}
              />
            </Section>

            <Section title="Company Address" icon={MapPin}>
              <Field
                label="Address Line 1"
                value={form.addressLine1}
                onChange={(value) => setField("addressLine1", value)}
                disabled={saving}
              />
              <Field
                label="Address Line 2"
                value={form.addressLine2}
                onChange={(value) => setField("addressLine2", value)}
                disabled={saving}
              />
              <Field
                label="City"
                value={form.city}
                onChange={(value) => setField("city", value)}
                disabled={saving}
              />
              <Field
                label="State"
                value={form.stateName}
                onChange={(value) => setField("stateName", value)}
                disabled={saving}
              />
              <Field
                label="PIN Code"
                value={form.postalCode}
                onChange={(value) => setField("postalCode", value)}
                disabled={saving}
              />
              <Field
                label="Country"
                value={form.country}
                onChange={(value) => setField("country", value)}
                disabled={saving}
              />
            </Section>

            <Section title="Tax Information" icon={FileText}>
              <Field
                label="GSTIN"
                value={form.gstin}
                onChange={(value) => setField("gstin", value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 15))}
                placeholder="33AAAAA0000A1Z5"
                disabled={saving}
                maxLength={15}
              />
              <Field
                label="PAN"
                value={form.pan}
                onChange={(value) => setField("pan", value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10))}
                placeholder="AAAAA0000A"
                disabled={saving}
                maxLength={10}
              />
            </Section>

            <Section title="Financial Year & Databases" icon={Database}>
              <div className="md:col-span-2">
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  Financial Year *
                </label>
                <select
                  value={form.financialYear}
                  onChange={(event) => setField("financialYear", event.target.value)}
                  disabled={saving}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none md:max-w-sm"
                >
                  {FINANCIAL_YEARS.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  {DB_TYPES.map((type) => (
                    <div key={type} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-xs font-bold text-emerald-700">{type}</span>
                        <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700">
                          WILL BE CREATED
                        </span>
                      </div>
                      <p className="break-all font-mono text-xs text-slate-700">
                        {databaseNames[type] || "Enter company name"}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="md:col-span-2">
                <div className="flex gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                  <Info size={18} className="mt-0.5 shrink-0" />
                  <p>
                    Databases will be restored from templates, login mapped, and data cleaned
                    (except tbl_country_master, tbl_state_master, tbl_city_master, tbl_district_master in Inventory) during registration.
                  </p>
                </div>
              </div>
            </Section>

            <Section title="License Information" icon={ShieldCheck}>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">License Plan *</span>
                <select
                  value={form.plan}
                  onChange={(event) => setField("plan", event.target.value)}
                  disabled={saving}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                >
                  <option value="STARTER">STARTER</option>
                  <option value="PRO">PRO</option>
                  <option value="ENTERPRISE">ENTERPRISE</option>
                </select>
              </label>
              <Field
                label="Maximum Users"
                type="number"
                value={form.maxUsers}
                onChange={(value) => setField("maxUsers", value)}
                min={1}
                required
                disabled={saving}
              />
              <Field
                label="License Expiry Date"
                type="date"
                value={form.expiresOn}
                onChange={(value) => setField("expiresOn", value)}
                disabled={saving}
              />
              <Field
                label="Grace Days"
                type="number"
                value={form.graceDays}
                onChange={(value) => setField("graceDays", value)}
                min={0}
                max={3650}
                required
                disabled={saving}
              />
              <div className="md:col-span-2">
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Features JSON</label>
                <textarea
                  value={form.featuresJson}
                  onChange={(event) => setField("featuresJson", event.target.value)}
                  rows={5}
                  disabled={saving}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-3 font-mono text-xs outline-none focus:border-emerald-500"
                />
              </div>
            </Section>

            {/* ========== NEW SECTION: Inventory Customer Options ========== */}
            <Section title="Inventory Customer Options" icon={Settings2}>
              <Field
                label="Site Code"
                value={form.fld_Site}
                onChange={(value) => setField("fld_Site", value)}
                disabled={saving}
              />
              <Field
                label="Site Name"
                value={form.fld_SiteName}
                onChange={(value) => setField("fld_SiteName", value)}
                disabled={saving}
              />
              <Field
                label="No. of Branches"
                type="number"
                value={form.fld_NoofBranches}
                onChange={(value) => setField("fld_NoofBranches", value)}
                min={0}
                disabled={saving}
              />
              <Field
                label="No. of Work Locations"
                type="number"
                value={form.fld_NoofWorkLocations}
                onChange={(value) => setField("fld_NoofWorkLocations", value)}
                min={0}
                disabled={saving}
              />
              <Field
                label="No. of Stores"
                type="number"
                value={form.fld_NoofStores}
                onChange={(value) => setField("fld_NoofStores", value)}
                min={0}
                disabled={saving}
              />
              <Field
                label="No. of Accounts Integration"
                type="number"
                value={form.fld_NoofAccountsIntegration}
                onChange={(value) => setField("fld_NoofAccountsIntegration", value)}
                min={0}
                disabled={saving}
              />
              <Field
                label="No. of Projects"
                type="number"
                value={form.fld_NoofProjects}
                onChange={(value) => setField("fld_NoofProjects", value)}
                min={0}
                disabled={saving}
              />
              <Field
                label="No. of Sub Branches"
                type="number"
                value={form.fld_NoofSubBranches}
                onChange={(value) => setField("fld_NoofSubBranches", value)}
                min={0}
                disabled={saving}
              />
              <Field
                label="No. of Products (Inventory)"
                type="number"
                value={form.fld_noofproductsinventory}
                onChange={(value) => setField("fld_noofproductsinventory", value)}
                min={0}
                disabled={saving}
              />
              <Field
                label="No. of Vehicles"
                type="number"
                value={form.fld_noofvehicle}
                onChange={(value) => setField("fld_noofvehicle", value)}
                min={0}
                disabled={saving}
              />
              <Field
                label="No. of Payroll"
                type="number"
                value={form.fld_noofpayroll}
                onChange={(value) => setField("fld_noofpayroll", value)}
                min={0}
                disabled={saving}
              />
              <Field
                label="No. of Assets"
                type="number"
                value={form.fld_noofasset}
                onChange={(value) => setField("fld_noofasset", value)}
                min={0}
                disabled={saving}
              />

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">Vehicle Connect Inventory</span>
                <select
                  value={form.fld_VehicleConnectInventory}
                  onChange={(e) => setField("fld_VehicleConnectInventory", Number(e.target.value))}
                  disabled={saving}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                >
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </select>
              </label>

              <Field
                label="Theme"
                value={form.fld_theme}
                onChange={(value) => setField("fld_theme", value)}
                disabled={saving}
              />
             <label className="block">
  <span className="mb-1.5 block text-sm font-medium text-slate-700">Language</span>
  <select
    value={form.fld_lang}
    onChange={(e) => setField("fld_lang", Number(e.target.value))}
    disabled={saving}
    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
  >
    <option value={0}>English</option>
    <option value={1}>Tamil</option>
    <option value={2}>Hindi</option>
  </select>
</label>
              <Field
                label="Mobile Path"
                value={form.fld_mobpath}
                onChange={(value) => setField("fld_mobpath", value)}
                disabled={saving}
              />
              <Field
                label="Desktop Path"
                value={form.fld_deskpath}
                onChange={(value) => setField("fld_deskpath", value)}
                disabled={saving}
              />
              <Field
                label="Mobile Domain"
                value={form.fld_mobdomain}
                onChange={(value) => setField("fld_mobdomain", value)}
                disabled={saving}
              />
              <Field
                label="Desktop Domain"
                value={form.fld_deskdomain}
                onChange={(value) => setField("fld_deskdomain", value)}
                disabled={saving}
              />

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">Job Card</span>
                <select value={form.fld_jobcard} onChange={(e) => setField("fld_jobcard", Number(e.target.value))} disabled={saving} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </select>
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">Bulk Insert</span>
                <select value={form.fld_bulkinsert} onChange={(e) => setField("fld_bulkinsert", Number(e.target.value))} disabled={saving} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </select>
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">Sunday Leave</span>
                <select value={form.fld_sundayleave} onChange={(e) => setField("fld_sundayleave", Number(e.target.value))} disabled={saving} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </select>
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">Multi User Block</span>
                <select value={form.fld_MultiUserBlock} onChange={(e) => setField("fld_MultiUserBlock", Number(e.target.value))} disabled={saving} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </select>
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">Inventory Dashboard Alert</span>
                <select value={form.fld_invdashboardalert} onChange={(e) => setField("fld_invdashboardalert", Number(e.target.value))} disabled={saving} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </select>
              </label>
              <Field
                label="Multi User Allow IP"
                value={form.fld_MultiUserAllowIP}
                onChange={(value) => setField("fld_MultiUserAllowIP", value)}
                disabled={saving}
              />
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">Terms Status</span>
                <select value={form.fld_termsstatus} onChange={(e) => setField("fld_termsstatus", Number(e.target.value))} disabled={saving} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </select>
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">Terms Visible</span>
                <select value={form.fld_termsVisible} onChange={(e) => setField("fld_termsVisible", Number(e.target.value))} disabled={saving} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </select>
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">Fin Year Allow Change</span>
                <select value={form.fld_finyearallowchange} onChange={(e) => setField("fld_finyearallowchange", Number(e.target.value))} disabled={saving} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </select>
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">Lockdown Monthly Attendance</span>
                <select value={form.fld_lockdownmonthlyAtten} onChange={(e) => setField("fld_lockdownmonthlyAtten", Number(e.target.value))} disabled={saving} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </select>
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">App Connection ERP</span>
                <select value={form.fld_appconnectionerp} onChange={(e) => setField("fld_appconnectionerp", Number(e.target.value))} disabled={saving} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </select>
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">App Connection Payroll</span>
                <select value={form.fld_appconnectionpayroll} onChange={(e) => setField("fld_appconnectionpayroll", Number(e.target.value))} disabled={saving} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </select>
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">Is Terms Link</span>
                <select value={form.fld_istermslink} onChange={(e) => setField("fld_istermslink", Number(e.target.value))} disabled={saving} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </select>
              </label>
              <Field
                label="Terms Link Name"
                value={form.fld_termslinkname}
                onChange={(value) => setField("fld_termslinkname", value)}
                disabled={saving}
              />
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">Video</span>
                <select value={form.fld_video} onChange={(e) => setField("fld_video", Number(e.target.value))} disabled={saving} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </select>
              </label>

              <div className="md:col-span-2">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                  Main DB, Active DB and FinYear DB Path are auto-filled from the generated database names above.
                </div>
              </div>
            </Section>

            <div className="flex flex-col items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:flex-row">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <CalendarDays size={16} />
                Databases will be provisioned and cleaned during registration.
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/supremeadmin/tenant")}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <ArrowLeft size={16} />
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  {saving ? <Loader2 size={17} className="animate-spin" /> : <Save size={17} />}
                  {saving ? "Registering & Provisioning..." : "Register Tenant"}
                </button>
              </div>
            </div>
          </form>
        </main>
      </div>
    </div>
  );
}